import { createContext, useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import Snackbar, { type SnackbarSeverity, type SnackbarAction } from './Snackbar'

/**
 * notistack 互換のキュー型 Snackbar 用 Provider と context value 定義。
 *
 * `useSnackbar()` から `enqueueSnackbar(message, options)` を呼ぶことで、Provider が
 * 内部 state にキュー要素を追加して同時に複数の {@link Snackbar} を render する。
 * autoHideDuration（または severity）に応じた自動非表示と、key 指定での明示 close を扱う。
 */

export type SnackbarKey = string

export interface EnqueueOptions {
  /** 既存 notistack 互換: default は info 同等にフォールバックする */
  variant?: SnackbarSeverity | 'default'
  autoHideDuration?: number | null
  anchorOrigin?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'center' | 'right'
  }
  /** notistack 互換: 真なら autoHideDuration=null と同義 */
  persist?: boolean
  /** 補助テキスト。指定時は Snackbar の message スロットへ反映 */
  description?: string
  /** Snackbar のアクションボタン群（ui/Snackbar の SnackbarAction） */
  actions?: SnackbarAction[]
}

export type EnqueueSnackbar = (message: string, options?: EnqueueOptions) => SnackbarKey

export interface SnackbarContextValue {
  enqueueSnackbar: EnqueueSnackbar
  closeSnackbar: (key?: SnackbarKey) => void
}

/**
 * Provider 未配置時のデフォルト値。notistack の `useSnackbar` 同様、エラーは投げず
 * 警告ログだけ出して no-op を返すことで、テスト用 renderHook など Provider を
 * 明示的にラップしない呼び出しに耐性を持たせる。
 */
const noopContextValue: SnackbarContextValue = {
  enqueueSnackbar: (message) => {
    if (process.env.NODE_ENV !== 'test') {
      console.warn('useSnackbar called outside SnackbarProvider, message dropped:', message)
    }
    return ''
  },
  closeSnackbar: () => {}
}

export const SnackbarContext = createContext<SnackbarContextValue>(noopContextValue)

interface SnackbarQueueItem {
  key: SnackbarKey
  message: string
  variant: SnackbarSeverity
  autoHideDuration: number | null | undefined
  anchorOrigin?: EnqueueOptions['anchorOrigin']
  description?: string
  actions?: SnackbarAction[]
}

interface SnackbarProviderProps {
  children: ReactNode
}

/**
 * notistack 互換の Snackbar 表示用 Provider。
 * 子コンポーネントから `useSnackbar()` 経由で `enqueueSnackbar` / `closeSnackbar` を呼べる。
 */
export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [items, setItems] = useState<SnackbarQueueItem[]>([])
  // key 衝突を避けるため発行時刻 + 連番でユニーク化する
  const counterRef = useRef(0)

  const closeSnackbar = useCallback((key?: SnackbarKey) => {
    setItems((prev) => {
      if (key == null) return []
      return prev.filter((item) => item.key !== key)
    })
  }, [])

  const enqueueSnackbar = useCallback((message: string, options: EnqueueOptions = {}): SnackbarKey => {
    counterRef.current += 1
    const key = `snackbar-${Date.now()}-${counterRef.current}`
    const variant: SnackbarSeverity = options.variant === 'default' || options.variant == null
      ? 'info'
      : options.variant

    // notistack 互換: persist 指定時は自動非表示なし
    const autoHideDuration = options.persist === true ? null : options.autoHideDuration

    setItems((prev) => [
      ...prev,
      {
        key,
        message,
        variant,
        autoHideDuration,
        anchorOrigin: options.anchorOrigin,
        description: options.description,
        actions: options.actions
      }
    ])
    return key
  }, [])

  const value = useMemo<SnackbarContextValue>(() => ({
    enqueueSnackbar,
    closeSnackbar
  }), [enqueueSnackbar, closeSnackbar])

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {items.map((item) => (
        <Snackbar
          actions={item.actions}
          anchorOrigin={item.anchorOrigin}
          autoHideDuration={item.autoHideDuration}
          key={item.key}
          message={item.description}
          onClose={() => closeSnackbar(item.key)}
          open
          severity={item.variant}
          title={item.message}
        />
      ))}
    </SnackbarContext.Provider>
  )
}
